// /payments/$txn_id — PayPal IPN transaction deep view (v1-B)
// Spec: docs/plans/admin-tooling.md §8.1, §8.3, §8.4
//
// Section A: D1 dps_accepted row (all 13 fields, labeled)
// Section B: R2 AuditBlob forensic detail (null-safe)
// Section C: related staff_audit rows for this buyer
// Section D: investigation note form (step-up required, actionIntent="payment-note")
//
// USD amounts: real-world PayPal USD, displayed with "$". NOT ¥/₢ (D-Currency-1
// applies to in-game cash/credits only, which do not appear on this surface).
// NO em-dash (U+2014) anywhere. Uses en-dash or hyphen only.

import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { Button, ErrorChip, PageHeader } from '@awakenedlands/ui';
import { StatusPill, paymentStatusToPill } from '@/components/admin/status-pill';
import { StepUpModal } from '@/components/admin/step-up-modal';

// ── Types ─────────────────────────────────────────────────────────────────────

interface DpsRow {
  dp_id: number;
  dp_buyer: number;
  dp_for: number;
  dp_type: number;
  dp_time: number;
  dp_txn: string;
  payment_status: string;
  payer_email: string;
  payment_fee: number;
  payment_type: string;
  payer_status: string;
  residence_country: string;
  amount_cents: number;
}

interface AuditParsed {
  item_name_label: string;
  buyer_userid: number;
  for_userid: number;
  pack: 1 | 2 | 3 | 4;
  payment_amount: string;
  payment_currency: string;
  payment_status: string;
  payer_email: string;
  receiver_email: string;
  payment_fee: string;
  payment_type: string;
  payer_status: string;
  residence_country: string;
}

interface AuditBlob {
  txn_id: string;
  received_at: string;
  raw_form_urlencoded: string;
  paypal_verify_response: 'VERIFIED' | 'INVALID' | 'validation_fail';
  parsed: AuditParsed | null;
  status: string;
  reason?: string;
  retry_count: number;
  last_retry_at: number | null;
  alerted_at: number | null;
}

interface AuditRow {
  id: number;
  staff_id: number;
  action: string;
  target_user_id: number | null;
  payload_json: string;
  ip: string;
  created_at: number;
  prev_hash: string | null;
  row_hash: string;
}

interface DeepViewResponse {
  ok: boolean;
  d1_row: DpsRow;
  r2_payload: AuditBlob | null;
  related_audit: AuditRow[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const _usdFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
function fmtUsd(cents: number): string {
  return _usdFormatter.format(cents / 100);
}

function fmtUtc(unixSec: number): string {
  return new Date(unixSec * 1000).toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
}

function fmtUtcOrNull(unixSec: number | null): string {
  if (unixSec === null) return '-';
  return fmtUtc(unixSec);
}

// ── Section A: D1 row ─────────────────────────────────────────────────────────

function D1Section({ row }: { row: DpsRow }) {
  return (
    <section
      className="rounded-sm border border-border bg-panel p-4 space-y-3"
      data-testid="d1-section"
    >
      <h2 className="text-sm font-semibold text-ink-high font-mono">D1 Record</h2>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs font-mono">
        <div><dt className="text-ink-low">dp_id</dt><dd className="text-ink-high tabular-nums">{row.dp_id}</dd></div>
        <div><dt className="text-ink-low">dp_txn</dt><dd className="text-ink-high break-all">{row.dp_txn}</dd></div>
        <div>
          <dt className="text-ink-low">dp_buyer</dt>
          <dd>
            <Link
              to="/users/$user_id/transactions"
              params={{ user_id: String(row.dp_buyer) }}
              className="text-primary underline underline-offset-2 tabular-nums"
            >
              #{row.dp_buyer}
            </Link>
          </dd>
        </div>
        <div><dt className="text-ink-low">dp_for</dt><dd className="text-ink-high tabular-nums">{row.dp_for}</dd></div>
        <div><dt className="text-ink-low">dp_type</dt><dd className="text-ink-high tabular-nums">{row.dp_type}</dd></div>
        <div>
          <dt className="text-ink-low">dp_time</dt>
          <dd className="text-ink-high tabular-nums">{fmtUtc(row.dp_time)}</dd>
        </div>
        <div>
          <dt className="text-ink-low">payment_status</dt>
          <dd className="mt-0.5" data-testid="d1-status-pill">
            <StatusPill status={paymentStatusToPill(row.payment_status)} />
          </dd>
        </div>
        <div>
          <dt className="text-ink-low">amount_cents</dt>
          <dd className="text-ink-high tabular-nums" data-testid="d1-amount">
            {fmtUsd(row.amount_cents)} ({row.amount_cents} cents)
          </dd>
        </div>
        <div><dt className="text-ink-low">payment_fee</dt><dd className="text-ink-high tabular-nums">{row.payment_fee}</dd></div>
        <div><dt className="text-ink-low">payment_type</dt><dd className="text-ink-high">{row.payment_type}</dd></div>
        <div><dt className="text-ink-low">payer_status</dt><dd className="text-ink-high">{row.payer_status}</dd></div>
        <div><dt className="text-ink-low">payer_email</dt><dd className="text-ink-high break-all">{row.payer_email}</dd></div>
        <div><dt className="text-ink-low">residence_country</dt><dd className="text-ink-high">{row.residence_country}</dd></div>
      </dl>
    </section>
  );
}

// ── Section B: R2 forensic detail ─────────────────────────────────────────────

function R2Section({ blob }: { blob: AuditBlob | null }) {
  if (!blob) {
    return (
      <section
        className="rounded-sm border border-border bg-panel p-4"
        data-testid="r2-section"
      >
        <h2 className="text-sm font-semibold text-ink-high font-mono mb-2">R2 Audit Blob</h2>
        <p className="text-xs text-ink-low" data-testid="r2-null-state">
          No R2 audit blob found for this transaction.
        </p>
      </section>
    );
  }

  return (
    <section
      className="rounded-sm border border-border bg-panel p-4 space-y-3"
      data-testid="r2-section"
    >
      <h2 className="text-sm font-semibold text-ink-high font-mono">R2 Audit Blob</h2>

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs font-mono">
        <div><dt className="text-ink-low">txn_id</dt><dd className="text-ink-high break-all">{blob.txn_id}</dd></div>
        <div><dt className="text-ink-low">received_at</dt><dd className="text-ink-high">{blob.received_at}</dd></div>
        <div>
          <dt className="text-ink-low">paypal_verify_response</dt>
          <dd className="text-ink-high" data-testid="r2-verify-response">{blob.paypal_verify_response}</dd>
        </div>
        <div>
          <dt className="text-ink-low">status</dt>
          <dd className="text-ink-high" data-testid="r2-status">{blob.status}</dd>
        </div>
        <div><dt className="text-ink-low">retry_count</dt><dd className="text-ink-high tabular-nums">{blob.retry_count}</dd></div>
        <div>
          <dt className="text-ink-low">last_retry_at</dt>
          <dd className="text-ink-high tabular-nums" data-testid="r2-last-retry">{fmtUtcOrNull(blob.last_retry_at)}</dd>
        </div>
        {blob.reason !== undefined && (
          <div className="col-span-full">
            <dt className="text-ink-low">reason</dt>
            <dd className="text-ink-high">{blob.reason}</dd>
          </div>
        )}
      </dl>

      {blob.parsed && (
        <div className="space-y-2">
          <p className="text-xs text-ink-mid font-mono font-semibold">Parsed fields</p>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs font-mono">
            <div><dt className="text-ink-low">payer_email</dt><dd className="text-ink-high break-all">{blob.parsed.payer_email}</dd></div>
            <div><dt className="text-ink-low">receiver_email</dt><dd className="text-ink-high break-all">{blob.parsed.receiver_email}</dd></div>
            <div><dt className="text-ink-low">payment_amount</dt><dd className="text-ink-high tabular-nums">{blob.parsed.payment_amount} {blob.parsed.payment_currency}</dd></div>
            <div><dt className="text-ink-low">payment_status</dt><dd className="text-ink-high">{blob.parsed.payment_status}</dd></div>
            <div><dt className="text-ink-low">payment_fee</dt><dd className="text-ink-high tabular-nums">{blob.parsed.payment_fee}</dd></div>
            <div><dt className="text-ink-low">payment_type</dt><dd className="text-ink-high">{blob.parsed.payment_type}</dd></div>
            <div><dt className="text-ink-low">payer_status</dt><dd className="text-ink-high">{blob.parsed.payer_status}</dd></div>
            <div><dt className="text-ink-low">residence_country</dt><dd className="text-ink-high">{blob.parsed.residence_country}</dd></div>
            <div><dt className="text-ink-low">pack</dt><dd className="text-ink-high tabular-nums">{blob.parsed.pack}</dd></div>
            <div><dt className="text-ink-low">item_name_label</dt><dd className="text-ink-high">{blob.parsed.item_name_label}</dd></div>
          </dl>
        </div>
      )}

      <div className="space-y-1">
        <p className="text-xs text-ink-mid font-mono font-semibold">Raw form-urlencoded payload</p>
        <pre
          className="rounded-sm border border-border bg-panel-elev p-3 text-xs font-mono text-ink-high overflow-x-auto whitespace-pre-wrap break-all"
          data-testid="r2-raw-payload"
        >
          {blob.raw_form_urlencoded.length > 4000
            ? `${blob.raw_form_urlencoded.slice(0, 4000)}... (truncated, ${blob.raw_form_urlencoded.length} chars total)`
            : blob.raw_form_urlencoded}
        </pre>
      </div>
    </section>
  );
}

// ── Section C: Related audit rows ─────────────────────────────────────────────

function RelatedAuditSection({ rows }: { rows: AuditRow[] }) {
  return (
    <section
      className="rounded-sm border border-border bg-panel p-4 space-y-3"
      data-testid="related-audit-section"
    >
      <h2 className="text-sm font-semibold text-ink-high font-mono">
        Related Audit ({rows.length})
      </h2>

      {rows.length === 0 && (
        <p className="text-xs text-ink-low" data-testid="related-audit-empty">
          No staff audit rows for this buyer.
        </p>
      )}

      {rows.length > 0 && (
        <>
          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto rounded-sm border border-border">
            <table className="w-full text-sm" data-testid="related-audit-table">
              <thead className="bg-muted text-left">
                <tr>
                  <th className="px-3 py-2 font-medium text-ink-mid">ID</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Date (UTC)</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Staff</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Action</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-divider">
                {rows.map((row) => (
                  <tr key={row.id} className="hover:bg-panel-elev" data-testid={`audit-row-${row.id}`}>
                    <td className="px-3 py-2 font-mono text-xs text-ink-low tabular-nums">{row.id}</td>
                    <td className="px-3 py-2 font-mono text-xs text-ink-low tabular-nums whitespace-nowrap">
                      {fmtUtc(row.created_at)}
                    </td>
                    <td className="px-3 py-2 tabular-nums text-xs">{row.staff_id}</td>
                    <td className="px-3 py-2 font-mono text-xs">{row.action}</td>
                    <td className="px-3 py-2 font-mono text-xs text-ink-low">{row.ip}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="flex flex-col gap-2 sm:hidden">
            {rows.map((row) => (
              <div
                key={row.id}
                className="rounded-sm border border-border bg-panel p-3 text-xs font-mono space-y-0.5"
                data-testid={`audit-card-${row.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="font-medium text-ink-high">{row.action}</span>
                  <span className="text-ink-low tabular-nums">#{row.id}</span>
                </div>
                <div className="text-ink-low">
                  Staff <span className="text-ink-mid">{row.staff_id}</span>
                  {' - '}{fmtUtc(row.created_at)}
                </div>
                <div className="text-ink-low">{row.ip}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

// ── Section D: Investigation note form ────────────────────────────────────────

interface NoteFormProps {
  txnId: string;
  buyerId: number;
  onNoteAdded: () => void;
}

function NoteForm({ txnId, buyerId, onNoteAdded }: NoteFormProps) {
  const [note, setNote] = useState('');
  const [showStepUp, setShowStepUp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const noteLen = note.length;
  const noteValid = noteLen >= 10 && noteLen <= 5000;

  function handleSubmit() {
    if (!noteValid) return;
    setShowStepUp(true);
  }

  async function handleVerified(token: string) {
    setShowStepUp(false);
    setSubmitting(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    const res = await apiFetch<{ ok: boolean; audit_row_id: number }>(
      `/admin/payments/transactions/${txnId}/note`,
      {
        method: 'POST',
        headers: { 'X-Stepup-Token': token },
        body: JSON.stringify({ note }),
      },
    );

    setSubmitting(false);

    if (!res.ok) {
      setErrorMsg('Failed to save note. Please try again.');
      return;
    }

    setSuccessMsg('Note saved.');
    setNote('');
    onNoteAdded();
  }

  return (
    <section
      className="rounded-sm border border-border bg-panel p-4 space-y-3"
      data-testid="note-form-section"
    >
      <h2 className="text-sm font-semibold text-ink-high font-mono">Investigation Note</h2>
      <p className="text-xs text-ink-low">
        Notes are written to the staff audit log (action: payment-note) and require step-up
        re-authentication. Min 10 chars, max 5000 chars.
      </p>

      <div className="space-y-2">
        <textarea
          className="w-full h-28 rounded-sm border border-border bg-panel px-3 py-2 text-sm text-ink-high font-mono resize-y focus:outline-none focus:ring-1 focus:ring-primary"
          placeholder="Enter investigation note..."
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={submitting}
          data-testid="note-textarea"
        />
        <div className="flex items-center justify-between gap-2">
          <span
            className={`text-xs font-mono tabular-nums ${noteLen > 5000 ? 'text-destructive-strong' : 'text-ink-low'}`}
            data-testid="note-char-count"
          >
            {noteLen} / 5000
          </span>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!noteValid || submitting}
            data-testid="note-submit-btn"
          >
            {submitting ? 'Saving...' : 'Save Note'}
          </Button>
        </div>
      </div>

      {errorMsg && (
        <ErrorChip data-testid="note-error">{errorMsg}</ErrorChip>
      )}
      {successMsg && (
        <span className="text-xs text-[--color-status-ok] font-mono" data-testid="note-success">
          {successMsg}
        </span>
      )}

      {showStepUp && (
        <StepUpModal
          actionIntent="payment-note"
          targetUserId={buyerId}
          onVerified={(token) => { void handleVerified(token); }}
          onCancel={() => setShowStepUp(false)}
        />
      )}
    </section>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function PaymentDetailPage() {
  const { txn_id } = useParams({ from: '/payments/$txn_id' });
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery<DeepViewResponse>({
    queryKey: ['admin-payment-detail', txn_id],
    queryFn: async () => {
      const res = await apiFetch<DeepViewResponse>(
        `/admin/payments/transactions/${txn_id}`,
      );
      if (!res.ok) throw new Error('Failed to load transaction.');
      return res.data;
    },
  });

  function handleNoteAdded() {
    // Refresh related audit section by invalidating the query.
    void queryClient.invalidateQueries({ queryKey: ['admin-payment-detail', txn_id] });
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/payments"
          className="text-xs text-ink-low underline underline-offset-2 hover:text-ink-high"
        >
          Back to Payments
        </Link>
      </div>

      <PageHeader
        title="Transaction Detail"
        description={`Forensic view for transaction ${txn_id}`}
      />

      {/* Loading */}
      {isLoading && (
        <div className="flex justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-ink-low border-t-primary" />
        </div>
      )}

      {/* Error */}
      {isError && (
        <ErrorChip data-testid="detail-error">Failed to load transaction.</ErrorChip>
      )}

      {/* Content */}
      {!isLoading && !isError && data && (
        <div className="space-y-4">
          {/* Section A: D1 row */}
          <D1Section row={data.d1_row} />

          {/* Section B: R2 forensic detail */}
          <R2Section blob={data.r2_payload} />

          {/* Section C: Related audit */}
          <RelatedAuditSection rows={data.related_audit} />

          {/* Section D: Investigation note */}
          <NoteForm
            txnId={txn_id}
            buyerId={data.d1_row.dp_buyer}
            onNoteAdded={handleNoteAdded}
          />
        </div>
      )}
    </div>
  );
}
